import React from 'react';
import { Form, Button, Message, Dimmer, Loader } from 'semantic-ui-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { UserDto, ChangeUserStore, NotificationCategory } from 'stores/users/users-store';
import { connect } from 'redux-scaffolding-ts';
import NotificationCategoriesSelector from 'widgets/form/notification-categories-selector';
import LineSeparator from 'widgets/bussiness/line-separator';
import { resolve } from 'inversify.config';
import { IdentityService } from 'services/identity-service';
import '../../../assets/less/user-settings.less';

interface UserSettingsPageProps extends WithTranslation {
  history?: any;
  changeUser?: ChangeUserStore;
}

interface NotificationSettings {
  eventReminders: boolean;
  statusUpdates: boolean;
  requestNotifications: boolean;
  systemAlerts: boolean;
}

interface UserSettingsPageState {
  currentUser: UserDto | null;
  emailNotifications: boolean;
  reportingGlobalNotifications: boolean;
  powerInstructorNotifications: boolean;
  notificationSettings: NotificationSettings;
  selectedNotificationTypes: NotificationCategory[];
  isSaving: boolean;
  isLoading: boolean;
  showSuccess: boolean;
  showError: boolean;
  errorMessage: string;
  initialEmailNotifications: boolean;
  initialReportingGlobalNotifications: boolean;
  initialPowerInstructorNotifications: boolean;
  initialNotificationSettings: NotificationSettings;
  initialSelectedNotificationTypes: NotificationCategory[];
}

@connect(['changeUser', ChangeUserStore])
class UserSettingsPage extends React.Component<UserSettingsPageProps, UserSettingsPageState> {
  @resolve(IdentityService)
  private identityService: IdentityService;

  private get changeUserStore() {
    return this.props.changeUser;
  }

  constructor(props: UserSettingsPageProps) {
    super(props);
    this.state = {
      currentUser: null,
      emailNotifications: false,
      reportingGlobalNotifications: false,
      powerInstructorNotifications: false,
      notificationSettings: {
        eventReminders: false,
        statusUpdates: false,
        requestNotifications: false,
        systemAlerts: false
      },
      selectedNotificationTypes: [],
      isSaving: false,
      isLoading: true,
      showSuccess: false,
      showError: false,
      errorMessage: '',
      initialEmailNotifications: false,
      initialReportingGlobalNotifications: false,
      initialPowerInstructorNotifications: false,
      initialNotificationSettings: {
        eventReminders: false,
        statusUpdates: false,
        requestNotifications: false,
        systemAlerts: false
      },
      initialSelectedNotificationTypes: []
    };
  }

  async componentDidMount() {
    await this.loadUserData();
  }

  private isGlobalReporting = (): boolean => {
    // Check if user has Reporting role with Global location
    const currentUser = this.state.currentUser;
    if (!currentUser || !currentUser.roles) {
      return false;
    }

    // Check if any role is Reporting with Global location
    const isGlobalReporting = currentUser.roles.some(
      roleInLocation => roleInLocation.role.name === 'Reporting' && roleInLocation.location && roleInLocation.location.location === 'Global'
    );

    return isGlobalReporting;
  };

  private hasInstructorRole = (): boolean => {
    // Check if user has Instructor role
    const currentUser = this.state.currentUser;
    if (!currentUser || !currentUser.roles) {
      return false;
    }

    // Check if any role is Instructor
    const isInstructor = currentUser.roles.some(roleInLocation => roleInLocation.role.name === 'Instructor');

    return isInstructor;
  };

  private loadUserData = async () => {
    try {
      this.setState({ isLoading: true });
      const currentUserDto = this.identityService.getCurrentUserDto();

      if (currentUserDto) {
        const defaultNotifications: NotificationSettings = {
          eventReminders: currentUserDto.emailNotifications,
          statusUpdates: currentUserDto.emailNotifications,
          requestNotifications: currentUserDto.emailNotifications,
          systemAlerts: false
        };

        const initialSelectedNotificationTypes = currentUserDto.selectedNotificationTypes || [];

        this.setState({
          currentUser: currentUserDto,
          emailNotifications: currentUserDto.emailNotifications,
          initialEmailNotifications: currentUserDto.emailNotifications,
          reportingGlobalNotifications: currentUserDto.reportingGlobalNotifications ?? true,
          initialReportingGlobalNotifications: currentUserDto.reportingGlobalNotifications ?? true,
          powerInstructorNotifications: currentUserDto.powerInstructorNotifications ?? false,
          initialPowerInstructorNotifications: currentUserDto.powerInstructorNotifications ?? false,
          notificationSettings: defaultNotifications,
          initialNotificationSettings: defaultNotifications,
          selectedNotificationTypes: initialSelectedNotificationTypes,
          initialSelectedNotificationTypes: [...initialSelectedNotificationTypes],
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      this.setState({
        isLoading: false,
        showError: true,
        errorMessage: 'Error loading user settings'
      });
    }
  };

  handleEmailNotificationsChange = (enabled: boolean) => {
    this.setState({
      emailNotifications: enabled,
      showSuccess: false,
      showError: false
    });
  };

  handleReportingGlobalNotificationsChange = (enabled: boolean) => {
    this.setState({
      reportingGlobalNotifications: enabled,
      showSuccess: false,
      showError: false
    });
  };

  handlePowerInstructorNotificationsChange = (enabled: boolean) => {
    this.setState({
      powerInstructorNotifications: enabled,
      showSuccess: false,
      showError: false
    });
  };

  handleSelectedNotificationTypesChange = (categories: NotificationCategory[]) => {
    this.setState({
      selectedNotificationTypes: categories,
      showSuccess: false,
      showError: false
    });
  };

  hasChanges = (): boolean => {
    const emailChanged = this.state.emailNotifications !== this.state.initialEmailNotifications;
    const reportingGlobalChanged = this.state.reportingGlobalNotifications !== this.state.initialReportingGlobalNotifications;
    const powerInstructorChanged = this.state.powerInstructorNotifications !== this.state.initialPowerInstructorNotifications;
    const notificationsChanged = JSON.stringify(this.state.notificationSettings) !== JSON.stringify(this.state.initialNotificationSettings);
    const selectedTypesChanged =
      JSON.stringify(this.state.selectedNotificationTypes) !== JSON.stringify(this.state.initialSelectedNotificationTypes);
    return emailChanged || reportingGlobalChanged || powerInstructorChanged || notificationsChanged || selectedTypesChanged;
  };

  handleSave = async () => {
    const { currentUser } = this.state;
    if (!currentUser) return;

    this.setState({ isSaving: true, showSuccess: false, showError: false, errorMessage: '' });

    try {
      // Convert roles to the format expected by ChangeUserDto
      const convertedRoles = currentUser.roles.map(role => ({
        roleName: role.role.name,
        locationId: role.location?.id || null,
        regionId: role.region?.id || null
      }));

      const updatedUser = {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        userName: currentUser.userName,
        email: currentUser.email,
        roles: convertedRoles,
        portrait: currentUser.portrait,
        locationId: currentUser.locationId,
        location: currentUser.location?.location || '',
        lineManager: currentUser.lineManager,
        enabled: currentUser.enabled,
        employeeId: currentUser.employeeId,
        positionCodeId: currentUser.positionCodeId,
        positionCode: currentUser.positionCode?.code || '',
        origin: currentUser.origin,
        region: currentUser.region,
        personnelAreaId: currentUser.personnelAreaId,
        personnelArea: currentUser.personnelArea,
        sfPosition: currentUser.sfPosition,
        updateFlag: currentUser.updateFlag,
        hireDate: currentUser.hireDate,
        leavingDate: currentUser.leavingDate,
        acronym: currentUser.acronym,
        languages: currentUser.languages || [],
        trainingLanguages: currentUser.trainingLanguages || [],
        hasProfileAssigned: currentUser.hasProfileAssigned,
        instructorRoleId: currentUser.instructorRoleId,
        isGlobalPoc: currentUser.isGlobalPoc,
        emailNotifications: this.state.emailNotifications,
        reportingGlobalNotifications: this.state.reportingGlobalNotifications,
        powerInstructorNotifications: this.hasInstructorRole() ? this.state.powerInstructorNotifications : false,
        notificationSettings: this.state.notificationSettings,
        selectedNotificationTypes: this.state.emailNotifications
          ? this.state.selectedNotificationTypes.map(category => ({ ...category, isSelected: false }))
          : this.state.selectedNotificationTypes
      };

      if (!this.changeUserStore) {
        throw new Error('ChangeUserStore is not available');
      }

      // First update the store's item state
      this.changeUserStore.change(updatedUser);

      // Then call updateUserEmailSettings to include emailSettings=true parameter
      const result = await this.changeUserStore.updateUserEmailSettings();

      if (result?.isSuccess) {
        // Update the identity service with the new user data
        const updatedUserDto: UserDto = {
          ...currentUser,
          emailNotifications: this.state.emailNotifications,
          reportingGlobalNotifications: this.state.reportingGlobalNotifications,
          powerInstructorNotifications: this.state.powerInstructorNotifications,
          selectedNotificationTypes: this.state.selectedNotificationTypes
        };
        this.identityService.setCurrentUserDto(updatedUserDto);

        this.setState({
          isSaving: false,
          showSuccess: true,
          showError: false,
          errorMessage: '',
          // Update initial values for the next comparison
          initialEmailNotifications: this.state.emailNotifications,
          initialReportingGlobalNotifications: this.state.reportingGlobalNotifications,
          initialPowerInstructorNotifications: this.state.powerInstructorNotifications,
          initialNotificationSettings: { ...this.state.notificationSettings },
          initialSelectedNotificationTypes: [...this.state.selectedNotificationTypes]
        });

        // Hide success message after 3 seconds
        setTimeout(() => {
          this.setState({ showSuccess: false });
        }, 3000);
      } else {
        this.setState({
          isSaving: false,
          showSuccess: false,
          showError: true,
          errorMessage: 'An error occurred while saving settings'
        });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      this.setState({
        isSaving: false,
        showSuccess: false,
        showError: true,
        errorMessage: 'An unexpected error occurred. Please try again.'
      });
    }
  };

  handleCancel = () => {
    const {
      initialEmailNotifications,
      initialReportingGlobalNotifications,
      initialPowerInstructorNotifications,
      initialNotificationSettings,
      initialSelectedNotificationTypes
    } = this.state;

    this.setState({
      emailNotifications: initialEmailNotifications,
      reportingGlobalNotifications: initialReportingGlobalNotifications,
      powerInstructorNotifications: initialPowerInstructorNotifications,
      notificationSettings: { ...initialNotificationSettings },
      selectedNotificationTypes: [...initialSelectedNotificationTypes],
      showError: false,
      showSuccess: false
    });
  };

  render() {
    const { t } = this.props;
    const {
      currentUser,
      emailNotifications,
      reportingGlobalNotifications,
      powerInstructorNotifications,
      selectedNotificationTypes,
      isSaving,
      isLoading,
      showSuccess,
      showError,
      errorMessage
    } = this.state;

    if (isLoading) {
      return (
        <>
          <h3>{t('User Settings')}</h3>
          <LineSeparator />
          <Dimmer active inverted>
            <Loader inverted>Loading user settings...</Loader>
          </Dimmer>
        </>
      );
    }

    if (!currentUser) {
      return (
        <>
          <h3>{t('User Settings')}</h3>
          <LineSeparator />
          <Message error>
            <Message.Header>Error</Message.Header>
            <p>Unable to load user settings. Please try refreshing the page.</p>
          </Message>
        </>
      );
    }

    const hasChanges = this.hasChanges();

    return (
      <>
        <Dimmer active={isSaving} page style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('Saving...')}</Loader>
        </Dimmer>

        <h3>{t('User Settings')}</h3>
        <LineSeparator />

        <div className="user-settings-wrapper user-settings-page">
          {showSuccess && (
            <Message success icon="check circle" header={t('Success')} content={t('Email notification settings updated successfully')} />
          )}

          {showError && <Message icon="exclamation circle" error header={t('An error occurred')} content={errorMessage} />}

          <Form>
            <div>
              <NotificationCategoriesSelector
                emailNotifications={emailNotifications}
                selectedNotificationTypes={selectedNotificationTypes}
                onEmailNotificationsChange={this.handleEmailNotificationsChange}
                onSelectedNotificationTypesChange={this.handleSelectedNotificationTypesChange}
                disabled={isSaving}
                userRoles={currentUser.roles?.map(role => role.role.name) || []}
                layout="single-column"
                isGlobalReporting={this.isGlobalReporting()}
                reportingGlobalNotifications={reportingGlobalNotifications}
                onReportingGlobalNotificationsChange={this.handleReportingGlobalNotificationsChange}
                isPowerInstructor={this.hasInstructorRole()}
                powerInstructorNotifications={powerInstructorNotifications}
                onPowerInstructorNotificationsChange={this.handlePowerInstructorNotificationsChange}
              />

              <Form.Group style={{ marginTop: '20px' }}>
                <Form.Field>
                  <Button onClick={this.handleSave} positive loading={isSaving} disabled={isSaving || !hasChanges}>
                    {t('Save')}
                  </Button>
                </Form.Field>
              </Form.Group>
            </div>
          </Form>
        </div>
      </>
    );
  }
}

export default withTranslation()(UserSettingsPage);
